import { z } from 'zod';
import i18n from '@shared/i18n';
import { FORM_FIELD_LIMITS } from '@/shared/utils/form-field-limits';
import { isValidInstagram, isValidPhone } from './schema.formats';

/**
 * Valid contact channel types. `phone_home` and `phone_work` are the
 * residential and business telephones (plan 2026-10-28, §5.3): telephones
 * like `phone`, with the same mask and validation.
 */
export const CONTACT_TYPES = ['email', 'phone', 'whatsapp', 'instagram', 'phone_home', 'phone_work'] as const;
export type ContactType = (typeof CONTACT_TYPES)[number];

/** Contact types holding a telephone number (backend `PHONE_CONTACT_TYPES`). */
export const PHONE_CONTACT_TYPES: readonly string[] = ['phone', 'whatsapp', 'phone_home', 'phone_work'];

/**
 * Whether a contact type holds a telephone number.
 *
 * @param type - Contact channel type.
 * @returns True for phone, WhatsApp, residential and business phones.
 */
export function isPhoneContactType(type: string): boolean {
  return PHONE_CONTACT_TYPES.includes(type);
}

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Translates a client-contact validation message key.
 *
 * @param key - i18n key under features/clients.
 * @param vars - Optional interpolation variables.
 * @returns The localized message string.
 */
const tContacts = (key: string, vars?: Record<string, unknown>) =>
  i18n.t(key, { ns: 'features/clients', ...vars });

/**
 * Max length for a contact value by channel type.
 *
 * @param type - Contact channel type (email, phone, whatsapp, instagram)
 * @returns Maximum allowed character count for the value
 */
export function contactValueMax(type: string): number {
  if (type === 'email') return FORM_FIELD_LIMITS.email;
  if (type === 'instagram') return FORM_FIELD_LIMITS.instagram;
  return FORM_FIELD_LIMITS.email;
}

type ContactMessages = {
  maxLength: (max: number) => string;
  invalidEmail: string;
  phoneMinDigits: string;
  invalidInstagram: string;
};

/**
 * Adds a max-length issue when the contact value is too long.
 *
 * @param type - Contact channel type
 * @param value - Contact value text
 * @param ctx - Zod refinement context
 * @param message - Localized error message
 */
function issueMaxLength(
  type: string,
  value: string,
  ctx: z.RefinementCtx,
  message: string,
): void {
  if (value.length <= contactValueMax(type)) return;
  ctx.addIssue({ code: z.ZodIssueCode.custom, message, path: ['value'] });
}

/**
 * Adds an invalid-email issue for email contacts with a bad format.
 *
 * @param type - Contact channel type
 * @param value - Contact value text
 * @param ctx - Zod refinement context
 * @param message - Localized error message
 */
function issueInvalidEmail(
  type: string,
  value: string,
  ctx: z.RefinementCtx,
  message: string,
): void {
  if (type !== 'email') return;
  if (emailRegex.test(value)) return;
  ctx.addIssue({ code: z.ZodIssueCode.custom, message, path: ['value'] });
}

/**
 * Adds a phone-length issue when the digit count falls outside the Brazilian
 * landline/mobile range (10-11 digits).
 *
 * @param type - Contact channel type
 * @param value - Contact value text
 * @param ctx - Zod refinement context
 * @param message - Localized error message
 */
function issuePhoneDigits(
  type: string,
  value: string,
  ctx: z.RefinementCtx,
  message: string,
): void {
  if (!isPhoneContactType(type)) return;
  if (isValidPhone(value)) return;
  ctx.addIssue({ code: z.ZodIssueCode.custom, message, path: ['value'] });
}

/**
 * Adds an invalid-handle issue for Instagram contacts with a bad format.
 *
 * @param type - Contact channel type
 * @param value - Contact value text
 * @param ctx - Zod refinement context
 * @param message - Localized error message
 */
function issueInvalidInstagram(
  type: string,
  value: string,
  ctx: z.RefinementCtx,
  message: string,
): void {
  if (type !== 'instagram') return;
  if (isValidInstagram(value)) return;
  ctx.addIssue({ code: z.ZodIssueCode.custom, message, path: ['value'] });
}

/**
 * Runs type-specific contact value validations against a Zod context.
 *
 * @param data - Contact type and value
 * @param ctx - Zod refinement context
 * @param messages - Localized validation messages
 */
function refineContactValue(
  data: { type: string; value: string },
  ctx: z.RefinementCtx,
  messages: ContactMessages,
): void {
  const { type, value } = data;
  issueMaxLength(type, value, ctx, messages.maxLength(contactValueMax(type)));
  issueInvalidEmail(type, value, ctx, messages.invalidEmail);
  issuePhoneDigits(type, value, ctx, messages.phoneMinDigits);
  issueInvalidInstagram(type, value, ctx, messages.invalidInstagram);
}

/**
 * Default messages resolved lazily, so the active language at validation time
 * wins instead of whatever i18n happened to hold at module evaluation.
 */
const defaultContactMessages: ContactMessages = {
  maxLength: (max) => tContacts('form.validation.maxLength', { max }),
  get invalidEmail() { return tContacts('form.validation.invalidEmail'); },
  get phoneMinDigits() { return tContacts('form.validation.phoneInvalid'); },
  get invalidInstagram() { return tContacts('form.validation.instagramInvalid'); },
};

/** Schema for a single contact entry with type-specific validation. */
export const contactEntrySchema = z.object({
  type: z.string().min(1),
  value: z.string().min(1),
  // Who answers this number ("Sogra Dilma"), kept from a spreadsheet
  // import; without it here, saving the drawer silently dropped it.
  label: z.string().optional(),
}).superRefine((data, ctx) => refineContactValue(data, ctx, defaultContactMessages));

type SchemaTranslate = (key: string, vars?: { max: number }) => string;

/**
 * Builds a localized contact entry schema for the client drawer form.
 *
 * @param t - Translation function scoped to features/clients
 * @returns Zod schema for a single contact entry
 */
export function createLocalizedContactEntry(t: SchemaTranslate) {
  const messages: ContactMessages = {
    maxLength: (max) => t('form.validation.maxLength', { max }),
    invalidEmail: t('form.validation.invalidEmail'),
    phoneMinDigits: t('form.validation.phoneInvalid'),
    invalidInstagram: t('form.validation.instagramInvalid'),
  };
  return z.object({
    type: z.string().min(1),
    value: z.string().min(1, t('form.validation.contactValueRequired')),
    label: z.string().optional(),
  }).superRefine((data, ctx) => refineContactValue(data, ctx, messages));
}
